import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { USER_addres } from '../../../API/API';

/**
 * Тройная капча для регистрации (2026-05-29):
 *   [левая 4-симв. кириллица] [нарисуй кота — canvas] [правая 4-симв. кириллица]
 *
 * Адаптировано под startklass: ходит напрямую в user-микросервис через USER_addres,
 * не зависит от локального UserService (которого тут нет — у startklass свой AuthService).
 *
 * collect() → { captchaIdLeft, captchaAnswerLeft, captchaIdRight, captchaAnswerRight, canvasData }
 * refresh() → перезагрузить пару капч (при ошибке сервера старые TTL=300с)
 */
const CANVAS_DESKTOP = { w: 220, h: 220 };
const CANVAS_MOBILE = { w: 140, h: 160 };

const TripleCaptcha = forwardRef(function TripleCaptcha({ disabled }, ref) {
    const [left, setLeft] = useState({ id: '', image: '' });
    const [right, setRight] = useState({ id: '', image: '' });
    const [answerLeft, setAnswerLeft] = useState('');
    const [answerRight, setAnswerRight] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isNarrow, setIsNarrow] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches
    );
    const dims = isNarrow ? CANVAS_MOBILE : CANVAS_DESKTOP;
    const CANVAS_W = dims.w;
    const CANVAS_H = dims.h;

    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef({ x: 0, y: 0 });
    const hasDrawnRef = useRef(false);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 700px)');
        const onChange = (e) => setIsNarrow(e.matches);
        try { mq.addEventListener('change', onChange); }
        catch { mq.addListener(onChange); }
        return () => {
            try { mq.removeEventListener('change', onChange); }
            catch { mq.removeListener(onChange); }
        };
    }, []);

    const loadPair = async () => {
        setLoading(true);
        setError('');
        try {
            const r = await axios.get(`${USER_addres}/api/v1/users/captcha/challenge-pair`);
            const p = r.data || {};
            setLeft({ id: p.left?.id || '', image: p.left?.image || '' });
            setRight({ id: p.right?.id || '', image: p.right?.image || '' });
            setAnswerLeft('');
            setAnswerRight('');
            clearCanvas();
        } catch {
            setError('Не удалось загрузить проверку. Обновите страницу.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPair();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const dpr = window.devicePixelRatio || 1;
        c.width = CANVAS_W * dpr;
        c.height = CANVAS_H * dpr;
        c.style.width = CANVAS_W + 'px';
        c.style.height = CANVAS_H + 'px';
        const ctx = c.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;
        hasDrawnRef.current = false;
    }, [CANVAS_W, CANVAS_H]);

    const clearCanvas = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
        hasDrawnRef.current = false;
    };

    const getPos = (e) => {
        const c = canvasRef.current;
        const r = c.getBoundingClientRect();
        const t = e.touches?.[0] || e.changedTouches?.[0];
        const cx = t ? t.clientX : e.clientX;
        const cy = t ? t.clientY : e.clientY;
        return { x: cx - r.left, y: cy - r.top };
    };

    const startDraw = (e) => {
        if (disabled) return;
        e.preventDefault();
        drawingRef.current = true;
        lastRef.current = getPos(e);
    };
    const moveDraw = (e) => {
        if (!drawingRef.current || disabled) return;
        e.preventDefault();
        const p = getPos(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastRef.current = p;
        hasDrawnRef.current = true;
    };
    const endDraw = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault?.();
        drawingRef.current = false;
    };

    useImperativeHandle(ref, () => ({
        collect: () => ({
            captchaIdLeft: left.id,
            captchaAnswerLeft: answerLeft.trim(),
            captchaIdRight: right.id,
            captchaAnswerRight: answerRight.trim(),
            canvasData: canvasRef.current ? canvasRef.current.toDataURL('image/png') : '',
        }),
        refresh: loadPair,
        isReady: () => Boolean(left.id && right.id && answerLeft.trim() && answerRight.trim() && hasDrawnRef.current),
    }));

    const wrapStyle = {
        border: '1px solid #d0d3da', borderRadius: 10, background: '#fafbfd',
        padding: 12, marginBottom: 16,
    };
    const rowStyle = {
        display: 'grid',
        gridTemplateColumns: `1fr ${CANVAS_W}px 1fr`,
        gap: 10, alignItems: 'start',
    };
    const colStyle = {
        display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0,
    };
    const imgStyle = {
        width: '100%', height: CANVAS_H - 40, borderRadius: 6,
        border: '1px solid #c8ccd5', background: '#f5f5f5',
        objectFit: 'contain', display: 'block',
    };
    const inputStyle = {
        width: '100%', padding: '10px 12px',
        border: '1px solid #c8ccd5', borderRadius: 6,
        fontSize: 18, boxSizing: 'border-box',
        textAlign: 'center', letterSpacing: 3, textTransform: 'uppercase',
        fontWeight: 600,
    };
    const canvasWrapStyle = {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    };
    const labelStyle = { fontSize: 13, color: '#374151', textAlign: 'center' };

    return (
        <div style={wrapStyle}>
            <div style={{ fontSize: 14, color: '#374151', marginBottom: 10 }}>
                Защита от ботов. Введите две капчи и нарисуйте кота:
            </div>
            <div style={rowStyle}>
                <div style={colStyle}>
                    {left.image ? (
                        <img src={left.image} alt="Капча" style={imgStyle} />
                    ) : (
                        <div style={{ ...imgStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                            {loading ? 'Загрузка…' : ''}
                        </div>
                    )}
                    <input
                        type="text" value={answerLeft}
                        onChange={(e) => setAnswerLeft(e.target.value)}
                        placeholder="Слева" disabled={disabled} maxLength={4}
                        autoComplete="off" style={inputStyle}
                    />
                </div>

                <div style={canvasWrapStyle}>
                    <div style={labelStyle}>Нарисуй кота</div>
                    <canvas
                        ref={canvasRef}
                        style={{
                            border: '1px solid #c8ccd5', borderRadius: 6,
                            background: '#ffffff', touchAction: 'none',
                            cursor: 'crosshair',
                        }}
                        onMouseDown={startDraw} onMouseMove={moveDraw}
                        onMouseUp={endDraw} onMouseLeave={endDraw}
                        onTouchStart={startDraw} onTouchMove={moveDraw}
                        onTouchEnd={endDraw}
                    />
                    <button
                        type="button" onClick={clearCanvas} disabled={disabled}
                        style={{
                            background: 'transparent', border: '1px solid #c8ccd5',
                            borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                            color: '#3357FF', fontSize: 12,
                        }}
                    >
                        Очистить
                    </button>
                </div>

                <div style={colStyle}>
                    {right.image ? (
                        <img src={right.image} alt="Капча" style={imgStyle} />
                    ) : (
                        <div style={{ ...imgStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                            {loading ? 'Загрузка…' : ''}
                        </div>
                    )}
                    <input
                        type="text" value={answerRight}
                        onChange={(e) => setAnswerRight(e.target.value)}
                        placeholder="Справа" disabled={disabled} maxLength={4}
                        autoComplete="off" style={inputStyle}
                    />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <button
                    type="button" onClick={loadPair} disabled={loading || disabled}
                    style={{
                        background: 'transparent', border: '1px solid #c8ccd5',
                        borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
                        color: '#3357FF', fontSize: 13,
                    }}
                >
                    Обновить картинки
                </button>
                {error && <div style={{ color: '#c00', fontSize: 13 }}>{error}</div>}
            </div>
        </div>
    );
});

export default TripleCaptcha;
